import type { CSSProperties } from 'react';
import type {
  FillGradient,
  FillMode,
  ObjectGlow,
  ObjectShadow,
  SceneObject,
} from '../types';
import {
  DEFAULT_FILL_GRADIENT,
  DEFAULT_OBJECT_GLOW,
  DEFAULT_OBJECT_SHADOW,
} from '../types/movie';

export function resolveFillMode(obj: Pick<SceneObject, 'fill' | 'fillMode'>): FillMode {
  if (obj.fillMode === 'solid' || obj.fillMode === 'gradient' || obj.fillMode === 'none') {
    return obj.fillMode;
  }
  return obj.fill === 'none' ? 'none' : 'solid';
}

export function parseFillMode(raw: unknown): FillMode | undefined {
  if (raw === 'solid' || raw === 'gradient' || raw === 'none') return raw;
  return undefined;
}

export function parseFillGradient(raw: unknown): FillGradient | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const angle = typeof record.angle === 'number' ? record.angle : DEFAULT_FILL_GRADIENT.angle;
  const colorStart =
    typeof record.colorStart === 'string' ? record.colorStart : DEFAULT_FILL_GRADIENT.colorStart;
  const colorEnd =
    typeof record.colorEnd === 'string' ? record.colorEnd : DEFAULT_FILL_GRADIENT.colorEnd;
  return { angle, colorStart, colorEnd };
}

export function parseObjectShadow(raw: unknown): ObjectShadow | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  return {
    enabled: record.enabled === true,
    offsetX: typeof record.offsetX === 'number' ? record.offsetX : DEFAULT_OBJECT_SHADOW.offsetX,
    offsetY: typeof record.offsetY === 'number' ? record.offsetY : DEFAULT_OBJECT_SHADOW.offsetY,
    blur: typeof record.blur === 'number' ? record.blur : DEFAULT_OBJECT_SHADOW.blur,
    spread: typeof record.spread === 'number' ? record.spread : DEFAULT_OBJECT_SHADOW.spread,
    color: typeof record.color === 'string' ? record.color : DEFAULT_OBJECT_SHADOW.color,
  };
}

export function parseObjectGlow(raw: unknown): ObjectGlow | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  return {
    enabled: record.enabled === true,
    blur: typeof record.blur === 'number' ? record.blur : DEFAULT_OBJECT_GLOW.blur,
    color: typeof record.color === 'string' ? record.color : DEFAULT_OBJECT_GLOW.color,
  };
}

export function mergeFillGradient(
  fill?: string,
  gradient?: FillGradient | null
): FillGradient {
  return {
    ...DEFAULT_FILL_GRADIENT,
    colorStart: fill && fill !== 'none' ? fill : DEFAULT_FILL_GRADIENT.colorStart,
    ...gradient,
  };
}

export function mergeObjectShadow(shadow?: ObjectShadow | null): ObjectShadow {
  return { ...DEFAULT_OBJECT_SHADOW, ...shadow };
}

export function mergeObjectGlow(glow?: ObjectGlow | null): ObjectGlow {
  return { ...DEFAULT_OBJECT_GLOW, ...glow };
}

/** CSS background for box shapes (rect/circle). */
export function buildFillBackground(
  obj: Pick<SceneObject, 'fill' | 'fillMode' | 'fillGradient'>
): { backgroundColor?: string; backgroundImage?: string } {
  const mode = resolveFillMode(obj);
  if (mode === 'none') {
    return { backgroundColor: 'transparent', backgroundImage: 'none' };
  }
  if (mode === 'gradient') {
    const g = mergeFillGradient(obj.fill, obj.fillGradient);
    return {
      backgroundColor: 'transparent',
      backgroundImage: `linear-gradient(${g.angle}deg, ${g.colorStart}, ${g.colorEnd})`,
    };
  }
  return {
    backgroundColor: obj.fill && obj.fill !== 'none' ? obj.fill : '#3b82f6',
    backgroundImage: 'none',
  };
}

/** Solid fill color or url(#id) for SVG shapes. */
export function buildSvgFill(
  obj: Pick<SceneObject, 'fill' | 'fillMode' | 'fillGradient'>,
  gradientId: string
): string {
  const mode = resolveFillMode(obj);
  if (mode === 'none') return 'none';
  if (mode === 'gradient') return `url(#${gradientId})`;
  return obj.fill && obj.fill !== 'none' ? obj.fill : '#3b82f6';
}

/** Convert CSS angle (0deg = up in CSS gradients… actually 0 = to top in older, in modern linear-gradient 0deg = to top).
 * SVG linearGradient uses x1,y1,x2,y2 in objectBoundingBox. */
export function gradientAngleToSvgCoords(angleDeg: number): {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
} {
  // CSS: 0deg = bottom→top, 90deg = left→right. Convert to unit vector.
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  const x = Math.cos(rad);
  const y = Math.sin(rad);
  return {
    x1: String(0.5 - x / 2),
    y1: String(0.5 - y / 2),
    x2: String(0.5 + x / 2),
    y2: String(0.5 + y / 2),
  };
}

export function buildTextFillStyles(
  obj: Pick<SceneObject, 'fill' | 'fillMode' | 'fillGradient'>
): CSSProperties {
  const mode = resolveFillMode(obj);
  if (mode === 'none') {
    return { color: 'transparent', backgroundImage: 'none' };
  }
  if (mode === 'gradient') {
    const g = mergeFillGradient(obj.fill, obj.fillGradient);
    return {
      color: 'transparent',
      backgroundImage: `linear-gradient(${g.angle}deg, ${g.colorStart}, ${g.colorEnd})`,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    };
  }
  return {
    color: obj.fill && obj.fill !== 'none' ? obj.fill : undefined,
    backgroundImage: 'none',
    WebkitTextFillColor: undefined,
  };
}

export type EffectStyleKind = 'box' | 'drop';

export function buildShadowFilterStyles(
  obj: Pick<SceneObject, 'shadow' | 'glow'>,
  kind: EffectStyleKind
): { boxShadow?: string; filter?: string } {
  const shadow = obj.shadow?.enabled ? mergeObjectShadow(obj.shadow) : null;
  const glow = obj.glow?.enabled ? mergeObjectGlow(obj.glow) : null;

  if (kind === 'box') {
    const layers: string[] = [];
    if (shadow) {
      layers.push(
        `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.spread}px ${shadow.color}`
      );
    }
    if (glow) {
      layers.push(`0 0 ${glow.blur}px 0 ${glow.color}`);
      layers.push(`0 0 ${Math.max(glow.blur * 1.6, glow.blur + 4)}px 0 ${glow.color}`);
    }
    return layers.length > 0 ? { boxShadow: layers.join(', ') } : {};
  }

  const filters: string[] = [];
  if (shadow) {
    filters.push(
      `drop-shadow(${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.color})`
    );
  }
  if (glow) {
    filters.push(`drop-shadow(0 0 ${Math.max(glow.blur * 0.45, 2)}px ${glow.color})`);
    filters.push(`drop-shadow(0 0 ${glow.blur}px ${glow.color})`);
  }
  return filters.length > 0 ? { filter: filters.join(' ') } : {};
}
